import { Component, type ReactNode, type ErrorInfo } from "react"
import {
  isStaleChunkError,
  recentlyAutoReloaded,
  scheduleAutoReload,
} from "@/lib/app-update"
import { telemetry } from "@/lib/telemetry/client"
import { redactPII } from "@/lib/telemetry/redact"

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isStaleChunk: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isStaleChunk: false }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isStaleChunk: isStaleChunkError(error),
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info)
    this.report(error, info)

    // Auto-recovery: se o erro for de chunk velho (post-deploy) E ainda não
    // tentamos recarregar há pouco, agenda reload silencioso. Caso contrário,
    // exibe a tela tradicional de erro com botão manual.
    if (this.state.isStaleChunk && !recentlyAutoReloaded()) {
      scheduleAutoReload(1500, error)
    }
  }

  /**
   * Reporta a quebra de tela para a telemetria.
   *
   * Por que isto não existia (e nada aparecia em `credit_v2_user_events`):
   * o TelemetryProvider escuta `window.onerror` / `unhandledrejection`, mas
   * o React 18, em build de produção, NÃO repassa ao `window.onerror` o erro
   * que um error boundary capturou — ele é tratado internamente e entregue
   * aqui. Resultado: exatamente a falha mais grave (a tela que não abre) era
   * a única invisível. Este é o único ponto do app onde ela passa.
   *
   * O que vai junto: rota (`page_path`, preenchido pelo client), papel de
   * quem sofreu, mensagem, stack e a pilha de componentes — tudo passado por
   * `redactPII`. Nome, CPF, telefone e e-mail do lead nunca entram.
   */
  private report(error: Error, info: ErrorInfo) {
    try {
      telemetry.track({
        event_type: "error",
        event_name: "error.render",
        properties: {
          role: telemetry.getRole(),
          name: redactPII(error?.name, 100),
          message: redactPII(error?.message, 500),
          stack: redactPII(error?.stack, 2000),
          component_stack: redactPII(info?.componentStack, 2000),
          stale_chunk: this.state.isStaleChunk,
        },
      })
      // Envia na hora: a tela de erro costuma terminar em recarregar a página,
      // e esperar o flush de 10s perderia justamente este evento.
      void telemetry.flush()
    } catch (reportErr) {
      // Telemetria nunca pode agravar uma tela já quebrada.
      console.warn("[telemetry] falha ao reportar erro de render", reportErr)
    }
  }

  render() {
    if (this.state.hasError) {
      // Caso 1: chunk antigo + ainda dentro da janela de auto-recovery → spinner
      if (this.state.isStaleChunk && !recentlyAutoReloaded()) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-[var(--base,#F5F5F7)]">
            <div className="text-center space-y-4 max-w-md px-6">
              <div className="flex justify-center">
                <div className="w-10 h-10 border-4 border-[var(--accent-blue,#7027FA)] border-t-transparent rounded-full animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-[var(--text-primary,#1D1D1F)]">
                Atualizando aplicação…
              </h2>
              <p className="text-sm text-[var(--text-secondary,#6E6E73)]">
                Uma nova versão foi publicada. Carregando automaticamente.
              </p>
            </div>
          </div>
        )
      }

      // Caso 2: erro real OU já tentou recarregar há pouco → tela amigável.
      // Pedido do PM (task #219): sem tela escura, sem mensagem técnica de
      // erro — só um botão bem visível que recarrega a página. Recarregar
      // resolve o cenário mais comum (versão nova publicada enquanto o
      // usuário estava com a antiga aberta).
      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--base,#F5F5F7)]">
          <div className="text-center space-y-8 max-w-sm w-full px-6">
            <img
              src="/logo.png"
              alt="CreditOS"
              className="h-8 w-auto mx-auto object-contain"
            />
            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2.5 px-6 py-4 rounded-2xl text-base font-semibold bg-[var(--accent-blue,#7027FA)] text-white shadow-lg shadow-[rgb(var(--accent-blue-rgb)/0.25)] hover:opacity-90 active:scale-[0.99] transition"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="shrink-0"
              >
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <path d="M21 3v6h-6" />
              </svg>
              Atualizar a página
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
